(() => {
  if (!customElements.get("chunks-v2-product")) {
    customElements.define(
      "chunks-v2-product",
      class extends HTMLElement {
        connectedCallback() {
          if (this.initialized) {
            this.observeGalleryVideos();
            return;
          }
          this.initialized = true;
          this.variants = JSON.parse(this.querySelector("[data-variants]").textContent);
          this.form = this.querySelector("form");
          this.media = [...this.querySelectorAll("[data-media]")];
          this.setupGalleryVideos();
          this.addEventListener("click", (event) => {
            const thumbnail = event.target.closest("[data-thumbnail]");
            if (thumbnail) this.showMedia(thumbnail.dataset.thumbnail);
            const arrow = event.target.closest("[data-gallery-step]");
            if (arrow && this.media.length) {
              const index = this.media.findIndex((item) => !item.hidden);
              this.showMedia(
                this.media[(index + Number(arrow.dataset.galleryStep) + this.media.length) % this.media.length].dataset
                  .media,
              );
            }
            const quantity = event.target.closest("[data-quantity-step]");
            if (quantity && this.form) {
              const input = this.form.elements.quantity;
              if (Number(quantity.dataset.quantityStep) > 0) input.stepUp();
              else input.stepDown();
              input.dispatchEvent(new Event("change", { bubbles: true }));
            }
          });
          if (!this.form) return;
          this.variantInput = this.form.elements.id;
          this.fieldsets = [...this.querySelectorAll("[data-option-index]")];
          const enhanced = this.querySelector(".cv2-enhanced-options");
          if (enhanced) enhanced.hidden = false;
          this.variantInput.hidden = true;
          this.fieldsets.forEach((fieldset) => fieldset.addEventListener("change", () => this.updateVariant(true)));
          this.updateVariant(false);
          this.form.addEventListener("submit", (event) => this.addToCart(event));
        }
        setupGalleryVideos() {
          this.galleryVideos = [...this.querySelectorAll(".cv2-stage video")];
          this.visibleVideos = new Set();
          this.galleryVideos.forEach((video) => {
            const slide = video.closest("[data-media]");
            const toggle = slide.querySelector("[data-gallery-video-toggle]");
            video.controls = false;
            video.muted = false;
            video.autoplay = false;
            video.playsInline = true;
            toggle.hidden = false;
            const updatePlayback = () => {
              slide.dataset.videoPlaying = String(!video.paused && !video.ended);
              toggle.setAttribute(
                "aria-label",
                video.paused || video.ended ? "Play product video" : "Pause product video",
              );
            };
            ["play", "pause", "ended", "error"].forEach((event) => video.addEventListener(event, updatePlayback));
            toggle.addEventListener("click", () => {
              if (video.paused || video.ended) {
                video.muted = false;
                video.play()?.catch(updatePlayback);
              } else {
                video.pause();
              }
            });
            updatePlayback();
          });
          this.onGalleryVisibility = () => this.galleryVideos.forEach((video) => this.syncGalleryVideo(video));
          this.observeGalleryVideos();
        }
        observeGalleryVideos() {
          if (!this.galleryVideos?.length) return;
          document.addEventListener("visibilitychange", this.onGalleryVisibility);
          this.galleryVideoObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
                if (isIntersecting && intersectionRatio >= 0.4) this.visibleVideos.add(target);
                else this.visibleVideos.delete(target);
                this.syncGalleryVideo(target);
              });
            },
            { threshold: [0, 0.4] },
          );
          this.galleryVideos.forEach((video) => this.galleryVideoObserver.observe(video));
        }
        syncGalleryVideo(video) {
          if (document.hidden || video.closest("[data-media]").hidden || !this.visibleVideos.has(video)) {
            video.pause();
          }
        }
        disconnectedCallback() {
          this.galleryVideoObserver?.disconnect();
          document.removeEventListener("visibilitychange", this.onGalleryVisibility);
          this.visibleVideos?.clear();
          this.galleryVideos?.forEach((video) => video.pause());
        }
        showMedia(id) {
          if (!this.media.some((item) => item.dataset.media === String(id))) return;
          this.media.forEach((item) => {
            item.hidden = item.dataset.media !== String(id);
            if (item.hidden) {
              item.querySelectorAll("video").forEach((video) => video.pause());
              item.querySelectorAll("iframe").forEach((frame) => {
                frame.src = frame.src;
              });
            }
          });
          this.querySelectorAll("[data-thumbnail]").forEach((button) =>
            button.setAttribute("aria-pressed", String(button.dataset.thumbnail === String(id))),
          );
        }
        updateVariant(updateURL) {
          const options = this.fieldsets.map((fieldset) => fieldset.querySelector("input:checked")?.value);
          const matches = (variant, values) => variant.options.every((value, index) => value === values[index]);
          const variant = this.fieldsets.length
            ? this.variants.find((item) => matches(item, options))
            : this.variants.find((item) => String(item.id) === this.variantInput.value);
          this.currentVariant = variant;
          this.fieldsets.forEach((fieldset, index) => {
            fieldset.querySelectorAll(".cv2-option").forEach((label) => {
              const values = [...options];
              values[index] = label.querySelector("input").value;
              const candidate = this.variants.find((item) => matches(item, values));
              label.dataset.unavailable = String(!candidate?.available);
              const price = label.querySelector("[data-option-price]");
              if (price) price.textContent = candidate ? candidate.price : "Unavailable";
            });
          });
          const button = this.form.querySelector('[name="add"]');
          button.disabled = !variant?.available || Boolean(this.busy);
          button.textContent = variant ? (variant.available ? "Add to cart" : "Sold out") : "Unavailable";
          this.variantInput.value = variant ? String(variant.id) : "";
          this.dispatchEvent(
            new CustomEvent("core:variant-change", {
              bubbles: true,
              detail: {
                variant: {
                  ...variant,
                  id: variant?.id || "",
                  available: Boolean(variant?.available),
                  price: variant?.price_cents || 0,
                  compare_at_price: variant?.compare_at_price || null,
                },
              },
            }),
          );
          const quantity = this.form.elements.quantity;
          if (variant) {
            quantity.min = variant.min;
            quantity.step = variant.step;
            if (variant.max) quantity.max = variant.max;
            else quantity.removeAttribute("max");
            const next = Math.max(
              variant.min,
              Math.ceil((Number(quantity.value) - variant.min) / variant.step) * variant.step + variant.min,
            );
            quantity.value = variant.max ? Math.min(next, variant.max) : next;
            if (updateURL) {
              const url = new URL(window.location.href);
              url.searchParams.set("variant", variant.id);
              history.replaceState({}, "", url);
              if (variant.media) this.showMedia(variant.media);
            }
          }
        }
        async addToCart(event) {
          event.preventDefault();
          if (this.busy || !this.currentVariant?.available || !this.form.reportValidity()) return;
          this.busy = true;
          const button = this.form.querySelector('[name="add"]');
          const status = this.querySelector("[data-form-status]");
          button.disabled = true;
          button.textContent = "Adding…";
          status.textContent = "";
          const root = window.Shopify?.routes?.root || "/";
          const formData = new FormData(this.form);
          let added = false;
          try {
            const response = await fetch(`${root}cart/add.js`, {
              method: "POST",
              body: formData,
              headers: { Accept: "application/json" },
            });
            const result = await response.clone().json();
            if (!response.ok) throw new Error(result.description || "Unable to add this item. Please try again.");
            added = true;
            status.textContent = "Added to your cart.";
            if (typeof window.upcartRefreshCart === "function") {
              await window.upcartRefreshCart();
            }
            if (typeof window.upcartOpenCart === "function") {
              await window.upcartOpenCart();
            } else if (typeof window.UpCart?.openCart === "function") {
              await window.UpCart.openCart();
            } else {
              throw new Error("Upcart is not ready.");
            }
          } catch (error) {
            status.textContent = added
              ? "Added to your cart. The cart could not open; please use the cart icon to view your items."
              : error.message || "Unable to add this item. Please try again.";
          } finally {
            this.busy = false;
            this.updateVariant(false);
            if (added) this.dispatchEvent(new CustomEvent("core:cart-add", {
              detail: { variantId: formData.get("id") }
            }));
          }
        }
      },
    );
  }
  if (!customElements.get("chunks-v2-love")) {
    customElements.define(
      "chunks-v2-love",
      class extends HTMLElement {
        connectedCallback() {
          if (this.initialized) return;
          this.initialized = true;
          this.track = this.querySelector("[data-story-track]");
          this.stories = [...this.querySelectorAll("[data-story]")];
          this.index = 0;
          const dots = this.querySelector(".cv2-dots");
          dots.hidden = this.stories.length < 2;
          this.querySelectorAll("[data-story-step]").forEach((button) => (button.hidden = this.stories.length < 2));
          const more = this.querySelector("[data-more-stories]");
          if (more) more.hidden = !this.querySelector("[data-play]");
          this.track.addEventListener(
            "scroll",
            () => {
              const start = this.track.getBoundingClientRect().left;
              this.index = this.stories.reduce(
                (best, story, i) =>
                  Math.abs(story.getBoundingClientRect().left - start) <
                  Math.abs(this.stories[best].getBoundingClientRect().left - start)
                    ? i
                    : best,
                0,
              );
              this.querySelectorAll("[data-story-index]").forEach((button) =>
                button.setAttribute("aria-pressed", String(Number(button.dataset.storyIndex) === this.index)),
              );
            },
            { passive: true },
          );
          this.addEventListener("click", (event) => {
            const step = event.target.closest("[data-story-step]");
            const dot = event.target.closest("[data-story-index]");
            if (step || dot) {
              const index = dot
                ? Number(dot.dataset.storyIndex)
                : (this.index + Number(step.dataset.storyStep) + this.stories.length) % this.stories.length;
              this.track.scrollTo({
                left:
                  this.track.scrollLeft +
                  this.stories[index].getBoundingClientRect().left -
                  this.track.getBoundingClientRect().left,
              });
            }
            const play = event.target.closest("[data-play]");
            if (play) this.play(play);
            if (event.target.closest("[data-more-stories]")) {
              const playable = this.stories
                .slice(this.index + 1)
                .concat(this.stories.slice(0, this.index + 1))
                .find((story) => story.querySelector("[data-play]"));
              if (playable) {
                this.track.scrollTo({
                  left:
                    this.track.scrollLeft +
                    playable.getBoundingClientRect().left -
                    this.track.getBoundingClientRect().left,
                });
                this.play(playable.querySelector("[data-play]"));
              }
            }
          });
        }
        play(button) {
          const media = button.closest(".cv2-story-media");
          let video = media.querySelector("video");
          if (!video) {
            const template = media.querySelector("template");
            if (!template) return;
            media.append(template.content.cloneNode(true));
            video = media.querySelector("video");
            if (!video) return;
            video.setAttribute("aria-label", button.getAttribute("aria-label"));
            video.addEventListener("play", () => {
              this.querySelectorAll("video").forEach((other) => {
                if (other !== video) other.pause();
              });
            });
          }
          button.hidden = true;
          this.index = this.stories.indexOf(button.closest("[data-story]"));
          video.focus({ preventScroll: true });
          video.play()?.catch(() => {
            // Native controls remain available if autoplay is blocked.
          });
        }
        disconnectedCallback() {
          this.querySelectorAll("video").forEach((video) => video.pause());
        }
      },
    );
  }
})();
